import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Pencil, Trash2, X, Search, Users, BookOpen, Link2,
ChevronDown,
  ChevronRight,
  Check,
  Baby,
  BookOpen as BookOpenIcon,
  School,
  Building,
  GraduationCap
 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Toast } from './ui/Toast';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import ClassAssociationModal from './ClassAssociationModal';
import LoadingSpinner from './LoadingSpinner';
import { CONFIRM_DIALOG_OVERLAY_CLASS } from './ui/modalOverlay';
import { BoiteACocher } from './ui/CaseACocher';
import { useParametres } from '../contexts/ParametresContext';
import {
  DROITS_FERMES,
  droitsVerifies,
  estRefusDeDroit,
  messageRefus,
  peutCreerMatieres,
  texteBandeauMatieres
} from './droitsClassesMatieres';

// ----------------------------------------------------------------------------
// Blocs déclarés dans le composant
// ----------------------------------------------------------------------------
// La pagination est déclarée à l'intérieur du composant. Écrite comme une
// simple fonction, React la voyait comme un composant neuf à chaque rendu et
// la reconstruisait. Ce crochet lui donne une identité stable, tout en lui
// faisant lire l'état à jour (même principe que ClassesManagement).
const useComposantStable = (rendu) => {
  const renduRef = useRef(rendu);
  renduRef.current = rendu;
  return useMemo(() => {
    const ComposantStable = (props) => renduRef.current(props);
    return ComposantStable;
  }, []);
};

const getActiveSchoolYearId = () => {
  const storedYear = localStorage.getItem('annee_scolaire_id');
  if (storedYear) {
    const parsed = parseInt(storedYear, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userYear = user.annee_scolaire_active || user.annee_scolaire_id;
    const parsed = parseInt(userYear, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
};
const sortByOrderThenName = (a, b) => {
  const orderA = Number(a?.ordre ?? 9999);
  const orderB = Number(b?.ordre ?? 9999);
  if (orderA !== orderB) return orderA - orderB;
  return String(a?.nom || a?.label || '').localeCompare(String(b?.nom || b?.label || ''), 'fr');
};

const ClassFilterDropdown = ({ value, classes, sections, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const selectedClass = classes.find(classe => String(classe.id) === String(value));

  const groupedClasses = useMemo(() => {
    const bySection = classes.reduce((acc, classe) => {
      const sectionId = classe.section_id || 'sans-section';
      if (!acc[sectionId]) acc[sectionId] = [];
      acc[sectionId].push(classe);
      return acc;
    }, {});

    const knownSections = [...sections]
      .filter(section => bySection[section.id]?.length > 0)
      .sort(sortByOrderThenName)
      .map(section => ({
        id: section.id,
        nom: section.nom,
        classes: [...bySection[section.id]].sort(sortByOrderThenName)
      }));

    const orphanClasses = bySection['sans-section'] || [];
    if (orphanClasses.length > 0) {
      knownSections.push({
        id: 'sans-section',
        nom: 'Sans section',
        classes: [...orphanClasses].sort(sortByOrderThenName)
      });
    }

    return knownSections;
  }, [classes, sections]);

  const filteredGroups = groupedClasses
    .map(group => ({
      ...group,
      classes: group.classes.filter(classe =>
        String(classe.nom || '').toLowerCase().includes(query.toLowerCase())
      )
    }))
    .filter(group => group.classes.length > 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left transition-colors focus:border-transparent focus:ring-2 focus:ring-[#246990]"
      >
        <span className={selectedClass ? 'text-gray-900' : 'text-gray-400'}>
          {selectedClass?.nom || 'Toutes les classes'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher une classe..."
                className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
                setQuery('');
              }}
              className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm font-medium hover:bg-[#f4fafc] ${!value ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'}`}
            >
              Toutes les classes
              {!value && <Check className="h-4 w-4" />}
            </button>

            {filteredGroups.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">Aucune classe trouvée</div>
            ) : (
              filteredGroups.map(group => (
                <div key={group.id}>
                  <div className="bg-[#eef7fb] px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#246990]">
                    {group.nom}
                  </div>
                  {group.classes.map(classe => (
                    <button
                      key={classe.id}
                      type="button"
                      onClick={() => {
                        onChange(String(classe.id));
                        setOpen(false);
                        setQuery('');
                      }}
                      className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-[#f4fafc] ${String(classe.id) === String(value) ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'}`}
                    >
                      <span className="font-medium">{classe.nom}</span>
                      {String(classe.id) === String(value) && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getMatiereClassCount = (matiere) => {
  const explicitCount = parseInt(matiere?.nombre_classes ?? 0, 10);
  if (Number.isFinite(explicitCount) && explicitCount > 0) return explicitCount;

  return String(matiere?.classes_ids || '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .length;
};

// `droits` vient de GET /api/classes/droits (ClasseMatieres) : fermé par
// défaut, il cache chaque geste d'écriture que le serveur refuserait.
const MatieresManagement = ({ estActif = true, droits = DROITS_FERMES, onRefusDroit } = {}) => {
  const { parametres, loading: loadingParametres } = useParametres();
  // Créer une matière demande deux droits : elle se crée avec au moins une
  // classe (handleCreateMatieres), donc avec ses associations.
  const creationOuverte = peutCreerMatieres(droits);
  // Le bandeau ne parle que de droits lus : si la lecture a échoué,
  // ClasseMatieres le dit lui-même, une seule fois.
  const bandeauMatieres = droitsVerifies(droits) ? texteBandeauMatieres(droits) : null;
  const activeYearId = getActiveSchoolYearId();
  const activeSections = useMemo(
    () => (parametres.sections || []).filter(section => section.active),
    [parametres.sections]
  );
  // ============================================================================
  // ÉTATS DE BASE
  // ============================================================================

  // États principaux
  const [classes, setClasses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  // Vrai jusqu'à la fin du premier chargement : la roue s'affiche d'emblée,
  // au lieu d'une liste vide suivie de la roue puis du contenu.
  const [chargementInitial, setChargementInitial] = useState(true);
  const [expandedSections, setExpandedSections] = useState(new Set());


  // Le titre de la fenêtre se calcule au rendu : il suit les droits, relus après
  // un refus, sans qu'on la rouvre.
  const [classAssociationModal, setClassAssociationModal] = useState({
  isOpen: false,
  editingMatiereId: null,
  initialSelections: [],
  matiereId: null,  // ⬅️ Doit être présent
  coefficientDefaut: 1,  // ⬅️ Doit être présent
  matiereNom: '',
  matiereCode: ''
});

  // États de recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [associationFilter, setAssociationFilter] = useState('all');
  const [filters, setFilters] = useState({
    classe_id: ''
  });

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // État d'édition
  const [editingItem, setEditingItem] = useState(null);

  // États pour les nouvelles matières
  const [newMatieres, setNewMatieres] = useState([{
    nom: '',
    code: '',
    coefficient: 1,
    selectedClasses: []
  }]);

  // États de sélection multiple (la sélection ne sert qu'à supprimer)
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const modeSelection = bulkMode && droits.matieres.supprimer;

  // États Toast
  const [toasts, setToasts] = useState([]);


// 2. AJOUTER CES ÉTATS (dans la section "ÉTATS DE BASE")
const [confirmDialog, setConfirmDialog] = useState({
  isOpen: false,
  title: '',
  description: '',
  onConfirm: null,
  type: 'warning'
});

  // ============================================================================
  // SYSTÈME DE TOAST
  // ============================================================================

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    const newToast = { id, message, type };

    setToasts(prev => {
      const withoutLoading = prev.filter(toast => toast.type !== 'loading');
      return [...withoutLoading, newToast];
    });

    if (type !== 'loading') {
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
      }, 5000);
    }

    return id;
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Le message du premier refus de permission parmi des réponses, ou null.
  // Les droits sont alors relus (onRefusDroit) : l'écran retire le geste qui
  // vient d'être refusé.
  const messageDuRefus = (resultats) => {
    const refus = resultats.find(resultat => estRefusDeDroit(resultat));
    if (!refus) return null;
    onRefusDroit?.();
    return messageRefus(refus);
  };


  const scrollToMatieresList = () => {
    requestAnimationFrame(() => {
      document.querySelector('[data-matieres-list]')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  };

  const applyAssociationFilter = (value) => {
    setAssociationFilter(value);
    setCurrentPage(1);
    setSelectedItems([]);
  };

  const showUnassignedMatieres = () => {
    setSearchTerm('');
    setFilters({ classe_id: '' });
    applyAssociationFilter('unassigned');
    scrollToMatieresList();
  };





  // ============================================================================
  // CALCULS STATISTIQUES
  // ============================================================================

  const totalMatieres = matieres.length;
  const classesWithMatieres = new Set(
    matieres.flatMap(matiere => String(matiere.classes_ids || '')
      .split(',')
      .map(id => parseInt(id, 10))
      .filter(id => !Number.isNaN(id)))
  ).size;
  const totalAssociations = matieres.reduce(
    (sum, matiere) => sum + getMatiereClassCount(matiere),
    0
  );
  const totalClasses = classes.length;
  const totalSections = sections.length;
  const matieresWithoutClasses = matieres.filter(matiere => !String(matiere.classes_ids || '').trim());
  const todoItems = [
    {
      key: 'matieres-unassigned',
      title: 'Matières non assignées',
      count: matieresWithoutClasses.length,
      detail: matieresWithoutClasses.slice(0, 3).map(matiere => matiere.nom).join(', '),
      action: showUnassignedMatieres,
      actionLabel: 'Afficher',
      color: 'border-amber-200 bg-amber-50 text-amber-800'
    }
  ].filter(item => item.count > 0);
  // ============================================================================
  // FONCTIONS PAGINATION
  // ============================================================================

  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };


  // 3. FONCTIONS HELPER POUR LE DIALOG
const openConfirmDialog = (config) => {
  setConfirmDialog({
    isOpen: true,
    ...config
  });
};

const closeConfirmDialog = () => {
  setConfirmDialog(prev => ({ ...prev, isOpen: false }));
};



const getSectionIcon = (sectionName) => {
  const name = sectionName.toLowerCase();
  if (name.includes('préscolaire') || name.includes('prescolaire') || name.includes('maternelle')) {
    return <Baby className="w-4 h-4" />;
  }
  if (name.includes('primaire') || name.includes('élémentaire')) {
    return <BookOpenIcon className="w-4 h-4" />;
  }
  if (name.includes('collège') || name.includes('college')) {
    return <School className="w-4 h-4" />;
  }
  if (name.includes('lycée') || name.includes('lycee') || name.includes('secondaire')) {
    return <Building className="w-4 h-4" />;
  }
  return <GraduationCap className="w-4 h-4" />;
};


const toggleExpansion = (sectionId) => {
  setExpandedSections(prev => {
    const newSet = new Set(prev);
    if (newSet.has(sectionId)) {
      newSet.delete(sectionId);
    } else {
      newSet.add(sectionId);
    }
    return newSet;
  });
};

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================

 const loadData = useCallback(async () => {
  setLoading(true);
  try {

    const [classesData, matieresData] = await Promise.all([
      api.classes.getAll(),
      api.matieres.getAll(activeYearId)
    ]);

    if (Array.isArray(classesData)) {
      setClasses(classesData);
    }
    if (Array.isArray(matieresData)) {
      setMatieres(matieresData);
    }

    setSections(activeSections);
  } catch (err) {
    console.error('Erreur rechargement:', err);
    addToast(err.message || 'Erreur lors du chargement', 'error');
  } finally {
    setLoading(false);
    setChargementInitial(false);
  }
}, [activeYearId, activeSections, addToast]);


 // Effet de chargement initial
useEffect(() => {
  if (!loadingParametres) {
    loadData();
  }
}, [loadData, loadingParametres]);

// L'onglet reste monté quand on passe sur Classes : à son retour, il se
// rafraîchit en arrière-plan, sans repasser par la roue de chargement.
const etaitActifRef = useRef(estActif);
useEffect(() => {
  if (estActif && !etaitActifRef.current && !loadingParametres) {
    loadData();
  }
  etaitActifRef.current = estActif;
}, [estActif, loadData, loadingParametres]);
 // Effet de chargement initial


  // ============================================================================
  // GESTION DES MATIÈRES
  // ============================================================================


  // 4. CRÉER LA FONCTION pour ouvrir le modal d'association
const openClassAssociationModal = async (matiere) => {
  try {
    setLoading(true);

    // Récupérer les associations actuelles
    const associations = await api.matieres.associations.getByMatiere(
      matiere.id,
      activeYearId
    );


    const associatedClassIds = associations.map(assoc => assoc.classe_id);

    setClassAssociationModal({
      isOpen: true,
      editingMatiereId: matiere.id,
      initialSelections: associatedClassIds,
      matiereId: matiere.id,  // ⬅️ AJOUTER
      coefficientDefaut: matiere.coefficient || 1,  // ⬅️ AJOUTER
      matiereNom: matiere.nom,
      matiereCode: matiere.code
    });

  } catch (error) {
    console.error('❌ Erreur récupération associations:', error);
    addToast('Erreur lors du chargement des associations', 'error');
  } finally {
    setLoading(false);
  }
};


const closeClassAssociationModal = () => {
  setClassAssociationModal({
    isOpen: false,
    editingMatiereId: null,
    initialSelections: [],
    matiereId: null,
    coefficientDefaut: 1,
    matiereNom: '',
    matiereCode: ''
  });
};

// 6. CRÉER LA FONCTION pour sauvegarder les associations
const saveClassAssociations = async (selectedClassIds, coefficients) => {
  if (!droits.associer) return { success: false };
  try {
    setLoading(true);
    addToast('Enregistrement des associations en cours...', 'loading');
    const matiereId = classAssociationModal.editingMatiereId;
    const failures = [];
    const echecs = [];

    const currentAssociations = await api.matieres.associations.getByMatiere(
      matiereId,
      activeYearId
    );
    const currentClassIds = currentAssociations.map(assoc => assoc.classe_id);

    const normalizeCoefficient = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const toAdd = selectedClassIds.filter(classId => !currentClassIds.includes(classId));
    const toRemove = currentClassIds.filter(classId => !selectedClassIds.includes(classId));
    const toUpdate = selectedClassIds.filter(classId => {
      const currentAssoc = currentAssociations.find(a => a.classe_id === classId);
      if (!currentAssoc) return false;
      const currentCoefficient = normalizeCoefficient(currentAssoc.coefficient_effectif);
      const newCoefficient = normalizeCoefficient(coefficients[classId] ?? classAssociationModal.coefficientDefaut);
      return currentCoefficient !== newCoefficient;
    });

    for (const classId of toAdd) {
      const addResult = await api.matieres.associations.add(
        matiereId,
        classId,
        activeYearId,
        coefficients[classId]
      );

      if (!addResult.success) {
        echecs.push(addResult);
        failures.push(addResult.message || "Erreur lors de l'ajout d'une association");
      }
    }

    for (const classId of toUpdate) {
      const currentAssoc = currentAssociations.find(a => a.classe_id === classId);
      if (!currentAssoc) continue;

      const updateResult = await api.matieres.associations.updateCoefficient(
        matiereId,
        classId,
        activeYearId,
        coefficients[classId]
      );

      if (!updateResult.success) {
        echecs.push(updateResult);
        failures.push(updateResult.message || "Erreur lors de la mise à jour d'un coefficient");
      }
    }

    for (const classId of toRemove) {
      const removeResult = await api.matieres.associations.remove(
        matiereId,
        classId,
        activeYearId
      );

      if (!removeResult.success) {
        echecs.push(removeResult);
        failures.push(removeResult.message || "Erreur lors du retrait d'une association");
      }
    }

    await loadData();

    const totalOperations = toAdd.length + toUpdate.length + toRemove.length;
    if (failures.length > 0) {
      const refus = messageDuRefus(echecs);
      // Refusé faute de permission : la fenêtre se ferme. Restée ouverte, elle
      // passerait en lecture seule (droits relus) en montrant encore, comme à
      // venir, des changements qui ne partiront pas.
      if (refus) closeClassAssociationModal();
      addToast(
        refus || (failures.length + ' opération(s) non finalisée(s). Vérifiez les associations.'),
        'error'
      );
      return { success: false, failures };
    }

    if (totalOperations > 0) {
      addToast(
        toAdd.length + ' ajoutée(s), ' + toUpdate.length + ' mise(s) à jour, ' + toRemove.length + ' supprimée(s)',
        'success'
      );
    } else {
      addToast('Aucune modification détectée', 'info');
    }

    closeClassAssociationModal();
    return { success: true };
  } catch (error) {
    console.error('Erreur sauvegarde associations:', error);
    addToast('Erreur lors de la sauvegarde des associations', 'error');
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

 const handleCreateMatieres = async () => {
  // ✅ Protection anti-spam
  if (loading || !creationOuverte) return;

  try {
    setLoading(true);
    const validMatieres = newMatieres.filter(m => {
      const coefficient = parseFloat(m.coefficient);
      return m.nom && m.code && m.selectedClasses.length > 0 && Number.isFinite(coefficient) && coefficient >= 0.5;
    });

    if (validMatieres.length === 0) {
      addToast('Veuillez remplir au moins une matière avec des classes associées', 'error');
      return;
    }

    addToast('Création des matières en cours...', 'loading');

    const createPromises = validMatieres.map(matiere => {
      const payload = {
        nom: matiere.nom,
        code: matiere.code,
        coefficient: parseFloat(matiere.coefficient),
        classes_selectionnees: matiere.selectedClasses,
        annee_scolaire_id: activeYearId
      };

      return api.matieres.create(payload);
    });

    const results = await Promise.all(createPromises);
    const successes = results.filter(r => r.success);
    const errors = results.filter(r => !r.success);

    if (successes.length > 0) {
      // ✅ Reset formulaire AVANT le reload
      setNewMatieres([{
        nom: '',
        code: '',
        coefficient: 1,
        selectedClasses: []
      }]);

      addToast(`${successes.length} matière(s) créée(s) avec succès`, 'success');

      if (errors.length > 0) {
        addToast(messageDuRefus(errors) || `${errors.length} erreur(s) lors de la création`, 'error');
      }

      // ✅ Reload seulement en cas de succès
      await loadData();
    } else {
      addToast(messageDuRefus(errors) || 'Erreur lors de la création des matières', 'error');
    }
  } catch (err) {
    console.error('❌ [FRONTEND] Erreur création:', err);
    addToast(err.message || 'Erreur lors de la création', 'error');
  } finally {
    setLoading(false); // ✅ Finally pour garantir l'arrêt du loading
  }
};



  // Suppression (unique ou multiple)
  const handleDelete = async (id) => {
  const matiere = matieres.find(m => m.id === id);

  openConfirmDialog({
    title: 'Supprimer la matière',
    description: `Voulez-vous vraiment supprimer la matière "${matiere?.nom}" ? Cette action est irréversible.`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler',
    type: 'error',
    onConfirm: async () => {
      // ✅ Protection anti-spam
      if (loading) return;
      closeConfirmDialog();

      try {
        setLoading(true);
        addToast('Suppression de la matière en cours...', 'loading');
        const result = await api.matieres.delete(id);

        if (result.success) {
          addToast('Matière supprimée avec succès', 'success');
          // ✅ Reload seulement en cas de succès
          await loadData();
        } else {
          addToast(messageDuRefus([result]) || result.message || 'Erreur lors de la suppression', 'error');
        }
      } catch (err) {
        addToast(err.message || 'Erreur lors de la suppression', 'error');
      } finally {
        setLoading(false);
      }
    }
  });
};


const handleBulkDelete = async () => {
  openConfirmDialog({
    title: 'Suppression multiple',
    description: `Voulez-vous vraiment supprimer ces ${selectedItems.length} matière(s) ? Cette action est irréversible.`,
    confirmText: 'Supprimer tout',
    cancelText: 'Annuler',
    type: 'error',
    onConfirm: async () => {
      // ✅ Protection anti-spam
      if (loading) return;
      closeConfirmDialog();

      try {
        setLoading(true);
        addToast('Suppression des matières sélectionnées en cours...', 'loading');
        const deletePromises = selectedItems.map(id => api.matieres.delete(id));
        const results = await Promise.all(deletePromises);

        const successes = results.filter(r => r.success);
        const echecs = results.filter(r => !r.success);

        if (successes.length > 0) {
          // ✅ Reset sélection AVANT reload
          setSelectedItems([]);
          addToast(`${successes.length} matière(s) supprimée(s)`, 'success');

          if (successes.length !== selectedItems.length) {
            addToast(messageDuRefus(echecs) || 'Certaines suppressions ont échouée', 'error');
          }

          // ✅ Reload seulement en cas de succès
          await loadData();
        } else {
          addToast(messageDuRefus(echecs) || 'Erreur lors de la suppression', 'error');
        }
      } catch (err) {
        addToast(err.message || 'Erreur lors de la suppression', 'error');
      } finally {
        setLoading(false);
      }
    }
  });
};

  // Mise à jour
   const handleUpdate = async (id, data) => {
  // ✅ Protection anti-spam
  if (loading) return;

  try {
    setLoading(true);
    addToast('Modification de la matière en cours...', 'loading');

    const result = await api.matieres.update(id, {
      nom: data.nom,
      code: data.code,
      coefficient: data.coefficient,
      annee_scolaire_id: activeYearId
    });

    if (result.success) {
      // ✅ Fermer modal AVANT le reload
      setEditingItem(null);
      addToast('Matière modifiée avec succès', 'success');

      // ✅ Reload seulement en cas de succès
      await loadData();
    } else {
      // Refusée faute de permission : la fenêtre se ferme, le toast dit pourquoi.
      const refus = messageDuRefus([result]);
      if (refus) setEditingItem(null);
      addToast(refus || result.message || 'Erreur lors de la modification', 'error');
    }
  } catch (err) {
    addToast(err.message || 'Erreur lors de la modification', 'error');
  } finally {
    setLoading(false);
  }
};
  // ============================================================================
  // FILTRAGE DES DONNÉES
  // ============================================================================

  const filteredData = matieres.filter(item => {
    const classIds = String(item.classes_ids || '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
    const matchesSearch = item.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesAssociationFilter = associationFilter === 'all' ||
      (associationFilter === 'assigned' ? classIds.length > 0 : classIds.length === 0);

    const matchesClassFilter = !filters.classe_id || classIds.includes(String(filters.classe_id));

    return matchesSearch && matchesAssociationFilter && matchesClassFilter;
  });

  // Pagination des données
  const hasActiveListFilters = Boolean(
    searchTerm ||
    associationFilter !== 'all' ||
    filters.classe_id
  );
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = paginateData(filteredData);

  useEffect(() => {
    const lastPage = Math.max(totalPages, 1);
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, totalPages]);

  // ============================================================================
  // COMPOSANT PAGINATION
  // ============================================================================

  const Pagination = useComposantStable(function Pagination({ currentPage, totalPages, onPageChange, onItemsPerPageChange, itemsPerPage }) {
    return (
      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 mt-4 px-6 py-4 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-2 text-sm text-gray-700">
          <span>Afficher</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              onItemsPerPageChange(Number(e.target.value));
            }}
            className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#319190]"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span>éléments par page</span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {"<<"}
          </button>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {"<"}
          </button>

          <div className="flex items-center">
            {Array.from({ length: totalPages }, (_, idx) => idx + 1)
              .filter(pageNumber => {
                return pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
              })
              .map((pageNumber, idx, array) => {
                if (idx > 0 && array[idx - 1] !== pageNumber - 1) {
                  return (
                    <React.Fragment key={`ellipsis-${pageNumber}`}>
                      <span className="px-2">...</span>
                      <button
                        onClick={() => onPageChange(pageNumber)}
                        className={`px-4 py-2 mx-1 rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-[#319190] text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={pageNumber}
                    onClick={() => onPageChange(pageNumber)}
                    className={`px-4 py-2 mx-1 rounded-md ${
                      currentPage === pageNumber
                        ? 'bg-[#319190] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {">"}
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {">>"}
          </button>
        </div>
      </div>
    );
  });



  // ============================================================================
  // DÉBUT DU RENDU PRINCIPAL
  // ============================================================================

  const isInitialLoading = loadingParametres || chargementInitial;

  if (isInitialLoading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4">
        <LoadingSpinner message="Chargement des matières..." color="#246990" fullHeight />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">
        {/* Header */}

        {/* Cartes de statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 px-6">
          <Card className="bg-gradient-to-r from-[#319190] to-[#63b5b4] text-white border-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-medium text-white/80">Total Matières</h3>
                  <p className="text-3xl font-bold mt-2">{totalMatieres}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <BookOpen className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-[#246990] to-[#3084b5] text-white border-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-medium text-white/80">Classes avec matières</h3>
                  <p className="text-3xl font-bold mt-2">{classesWithMatieres}</p>
                  <p className="text-sm text-white/80 mt-1">Sur {totalClasses} total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-[#c66d59] to-[#ae7467] text-white border-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-medium text-white/80">Associations</h3>
                  <p className="text-3xl font-bold mt-2">{totalAssociations}</p>
                  <p className="text-sm text-white/80 mt-1">classe-matière</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <School className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-gray-700 to-gray-800 text-white border-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-medium text-white/80">Sections actives</h3>
                  <p className="text-3xl font-bold mt-2">{totalSections}</p>
                  <p className="text-sm text-white/80 mt-1">configurées</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Building className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="px-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-gray-900">À traiter</CardTitle>
                  <CardDescription>Matières à associer pour finaliser la configuration pédagogique.</CardDescription>
                </div>
                {todoItems.length === 0 && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                    Associations à jour
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {todoItems.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {todoItems.map(item => (
                    <div key={item.key} className={`border rounded-lg p-4 ${item.color}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="text-2xl font-bold mt-1">{item.count}</p>
                        </div>
                        {item.action && (
                          <button
                            type="button"
                            onClick={item.action}
                            className="px-3 py-1 rounded-md bg-white/80 text-xs font-medium hover:bg-white transition-colors"
                          >
                            {item.actionLabel}
                          </button>
                        )}
                      </div>
                      <p className="text-xs mt-3 min-h-[32px]">
                        {item.detail || 'Aucun détail disponible'}{item.count > 3 ? '...' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Toutes les matières sont associées à au moins une classe.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recherche et filtres */}
        <div className="px-6">
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-end">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Rechercher une matière</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Nom ou code de la matière..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                        setSelectedItems([]);
                      }}
                      className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Classe associée</label>
                  <ClassFilterDropdown
                    value={filters.classe_id}
                    classes={classes}
                    sections={sections}
                    onChange={(classeId) => {
                      setFilters(prev => ({ ...prev, classe_id: classeId }));
                      setCurrentPage(1);
                      setSelectedItems([]);
                    }}
                  />
                </div>

              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {[
                  { value: 'all', label: 'Toutes', count: totalMatieres },
                  { value: 'unassigned', label: 'Non assignées', count: matieresWithoutClasses.length },
                  { value: 'assigned', label: 'Assignées', count: Math.max(totalMatieres - matieresWithoutClasses.length, 0) }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => applyAssociationFilter(option.value)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      associationFilter === option.value
                        ? 'border-[#246990] bg-[#246990] text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label} ({option.count})
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>


{/* Formulaire d'ajout de matières : une matière se crée avec au moins une
    classe, il faut donc aussi le droit d'associer. */}
        {creationOuverte && (
        <div className="px-6" data-formulaire>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-[#319190]">Ajouter des matières</CardTitle>
              <CardDescription>
                Créez de nouvelles matières et associez-les aux classes appropriées
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {newMatieres.map((matiere, index) => (
                <div key={index} className="space-y-4 p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  {/* En-tête de matière */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">
                      Matière {index + 1}
                    </h4>
                    {newMatieres.length > 1 && (
                      <button
                        onClick={() => {
                          const updated = newMatieres.filter((_, i) => i !== index);
                          setNewMatieres(updated);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Informations de base */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de la matière *
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Mathématiques, Français..."
                        value={matiere.nom}
                        onChange={(e) => {
                          const updated = [...newMatieres];
                          updated[index].nom = e.target.value;
                          setNewMatieres(updated);
                        }}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#319190] focus:ring-1 focus:ring-[#319190] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code matière *
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: MATH, FR, ENG..."
                        value={matiere.code}
                        onChange={(e) => {
                          const updated = [...newMatieres];
                          updated[index].code = e.target.value.toUpperCase();
                          setNewMatieres(updated);
                        }}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#319190] focus:ring-1 focus:ring-[#319190] outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Coefficient *
                      </label>
                      <input
                        type="number"
                        placeholder="1"
                        value={matiere.coefficient}
                        onChange={(e) => {
                          const updated = [...newMatieres];
                          updated[index].coefficient = parseFloat(e.target.value) || 1;
                          setNewMatieres(updated);
                        }}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#319190] focus:ring-1 focus:ring-[#319190] outline-none"
                        min="0.5"
                        step="0.5"
                      />
                    </div>
                  </div>

                  {/* Sélection des classes */}
                  <div>
  <label className="block text-sm font-medium text-gray-700 mb-3">
    Classes associées *
  </label>

  {(() => {
    // Grouper les classes par section
    const classesBySection = classes.reduce((acc, classe) => {
      const sectionId = classe.section_id;
      if (!acc[sectionId]) {
        acc[sectionId] = [];
      }
      acc[sectionId].push(classe);
      return acc;
    }, {});

    // Trier les sections par ordre
    const sortedSections = sections.sort((a, b) => {
      if (a.ordre && b.ordre) return a.ordre - b.ordre;
      if (a.ordre) return -1;
      if (b.ordre) return 1;
      return a.nom.localeCompare(b.nom);
    });

    // Fonctions helper pour cette matière
    const isSectionFullySelected = (sectionId) => {
      const sectionClasses = classesBySection[sectionId] || [];
      return sectionClasses.length > 0 &&
             sectionClasses.every(classe => matiere.selectedClasses.includes(classe.id));
    };

    const isSectionPartiallySelected = (sectionId) => {
      const sectionClasses = classesBySection[sectionId] || [];
      return sectionClasses.some(classe => matiere.selectedClasses.includes(classe.id)) &&
             !isSectionFullySelected(sectionId);
    };

    const toggleSection = (sectionId) => {
      const sectionClasses = classesBySection[sectionId] || [];
      const sectionClassIds = sectionClasses.map(c => c.id);
      const updated = [...newMatieres];

      if (isSectionFullySelected(sectionId)) {
        // Désélectionner toute la section
        updated[index].selectedClasses = updated[index].selectedClasses.filter(
          id => !sectionClassIds.includes(id)
        );
      } else {
        // Sélectionner toute la section
        sectionClassIds.forEach(id => {
          if (!updated[index].selectedClasses.includes(id)) {
            updated[index].selectedClasses.push(id);
          }
        });
      }
      setNewMatieres(updated);
    };

    const toggleClass = (classId) => {
      const updated = [...newMatieres];
      if (updated[index].selectedClasses.includes(classId)) {
        updated[index].selectedClasses = updated[index].selectedClasses.filter(
          id => id !== classId
        );
      } else {
        updated[index].selectedClasses = [...updated[index].selectedClasses, classId];
      }
      setNewMatieres(updated);
    };

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {sortedSections.map(section => {
          const sectionClasses = classesBySection[section.id] || [];
          const isExpanded = expandedSections.has(section.id);
          const isFullySelected = isSectionFullySelected(section.id);
          const isPartiallySelected = isSectionPartiallySelected(section.id);

          if (sectionClasses.length === 0) return null;

          return (
            <div key={section.id} className="border-b border-gray-100 last:border-b-0">
              {/* En-tête de section */}
              <div
                className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25"
                onClick={() => toggleExpansion(section.id)}
              >
                <div className="flex items-center space-x-3">
                  {/* Icône d'expansion */}
                  <div className="text-gray-400">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>

                  {/* Icône de section */}
                  <div className="text-[#319190]">
                    {getSectionIcon(section.nom)}
                  </div>

                  {/* Nom de section */}
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{section.nom}</h4>
                    <p className="text-xs text-gray-500">
                      {sectionClasses.length} classe{sectionClasses.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Checkbox de section */}
                <div className="flex items-center space-x-2">
                  {isPartiallySelected && (
                    <span className="text-xs text-orange-600 font-medium">
                      Partielle
                    </span>
                  )}
                  {/* La case de KLASYS (ui/CaseACocher, DESIGN-SYSTEM.md), partielle
                      quand une partie seulement des classes est cochée. */}
                  <label
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BoiteACocher
                      checked={isFullySelected}
                      indetermine={isPartiallySelected}
                      onChange={() => toggleSection(section.id)}
                    />
                    <span className={`text-sm font-medium ${isFullySelected ? 'text-gray-900' : 'text-gray-700'}`}>
                      Tout
                    </span>
                  </label>
                </div>
              </div>

              {/* Classes de la section */}
              {isExpanded && (
                <div className="bg-white p-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {sectionClasses
                      .sort((a, b) => {
                        if (a.ordre && b.ordre) return a.ordre - b.ordre;
                        return a.nom.localeCompare(b.nom);
                      })
                      .map(classe => {
                        const isSelected = matiere.selectedClasses.includes(classe.id);
                        return (
                          <label
                            key={classe.id}
                            className={`flex items-center space-x-2 p-2 rounded border cursor-pointer transition-all text-sm ${
                              isSelected
                                ? 'border-[#319190] bg-[#319190]/5'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {/* La case de KLASYS (ui/CaseACocher) : toute la ligne la coche. */}
                            <BoiteACocher
                              checked={isSelected}
                              onChange={() => toggleClass(classe.id)}
                            />
                            {/* Le nom seul : l'effectif n'aide pas à associer une matière (demande de Daniel du 01/11/2026). */}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate text-sm">
                                {classe.nom}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className="w-3 h-3 text-[#319190]" />
                            )}
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  })()}

  {/* Indicateur de sélection mis à jour */}
  <div className="mt-3 flex items-center justify-between text-sm">
    <div className="flex items-center space-x-4">
      <span className="text-gray-600">
        {matiere.selectedClasses.length} classe{matiere.selectedClasses.length > 1 ? 's' : ''} sélectionnée{matiere.selectedClasses.length > 1 ? 's' : ''}
      </span>
      <span className="text-gray-500">
        dans {new Set(
          matiere.selectedClasses.map(classId => {
            const classe = classes.find(c => c.id === classId);
            return classe?.section_id;
          }).filter(Boolean)
        ).size} section{new Set(
          matiere.selectedClasses.map(classId => {
            const classe = classes.find(c => c.id === classId);
            return classe?.section_id;
          }).filter(Boolean)
        ).size > 1 ? 's' : ''}
      </span>
    </div>

    {/* Actions rapides globales */}
    <div className="flex items-center space-x-2">
      <button
        type="button"
        onClick={() => {
          const updated = [...newMatieres];
          updated[index].selectedClasses = classes.map(c => c.id);
          setNewMatieres(updated);
          // Ouvrir toutes les sections pour voir la sélection
          setExpandedSections(new Set(sections.map(s => s.id)));
        }}
        className="text-[#319190] hover:text-[#63b5b4] font-medium text-xs"
      >
        Tout sélectionner
      </button>
      <span className="text-gray-300">|</span>
      <button
        type="button"
        onClick={() => {
          const updated = [...newMatieres];
          updated[index].selectedClasses = [];
          setNewMatieres(updated);
        }}
        className="text-gray-600 hover:text-gray-800 text-xs"
      >
        Tout déselectionner
      </button>
    </div>
  </div>
</div>

                  {/* Aperçu de création */}
                  {matiere.nom && matiere.code && matiere.selectedClasses.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                        <p className="font-medium text-green-800">Pret à créer</p>
                      </div>
                      <p className="text-sm text-green-700">
                        La matière "{matiere.nom}" ({matiere.code}) sera crée avec un coefficient de {matiere.coefficient}
                        dans {matiere.selectedClasses.length} classe(s).
                      </p>
                    </div>
                  )}
                </div>
              ))}

              {/* Actions formulaire */}
              <div className="flex gap-4 pt-6 border-t">
                <button
                  onClick={() => setNewMatieres([...newMatieres, {
                    nom: '',
                    code: '',
                    coefficient: 1,
                    selectedClasses: []
                  }])}
                  className="flex items-center text-[#319190] hover:text-[#63b5b4] font-medium transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une matière
                </button>

                <button
                  onClick={handleCreateMatieres}
                  disabled={loading || newMatieres.every(m => !m.nom || !m.code || m.selectedClasses.length === 0 || !Number.isFinite(parseFloat(m.coefficient)) || parseFloat(m.coefficient) < 0.5)}
                  className="flex items-center px-6 py-2 bg-[#319190] text-white rounded-lg hover:bg-[#63b5b4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer les matières
                    </>
                  )}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Table des matières */}
        <div className="px-6 space-y-4" data-matieres-list>
          {bandeauMatieres && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">{bandeauMatieres}</p>
          )}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-xl text-[#319190]">
                    Liste des matières
                  </CardTitle>
                  <CardDescription>
                    {filteredData.length} matière(s) trouvée(s) sur {totalMatieres} total
                  </CardDescription>
                </div>

                {droits.matieres.supprimer && (
                <div className="flex flex-wrap items-center gap-3">
                  {bulkMode && (
                    <span className="rounded-lg border border-[#246990]/15 bg-[#246990]/5 px-3 py-2 text-sm font-medium text-[#246990]">
                      {selectedItems.length} sélectionnée(s)
                    </span>
                  )}

                  {bulkMode && selectedItems.length > 0 && (
                    <button
                      type="button"
                      onClick={handleBulkDelete}
                      className="inline-flex items-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer ({selectedItems.length})
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      const nextBulkMode = !bulkMode;
                      setBulkMode(nextBulkMode);
                      if (!nextBulkMode) {
                        setSelectedItems([]);
                      }
                    }}
                    className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      bulkMode
                        ? 'bg-[#246990] text-white shadow-sm'
                        : 'border border-gray-300 bg-white text-gray-700 hover:border-[#246990]/30 hover:text-[#246990]'
                    }`}
                  >
                    {bulkMode && <Check className="mr-2 h-4 w-4" />}
                    Sélection multiple
                  </button>
                </div>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {filteredData.length === 0 ? (
                // État vide
                <div className="p-12 text-center">
                  <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {hasActiveListFilters ? 'Aucun résultat trouvé' : 'Aucune matière créée'}
                  </h3>
                  <p className="text-gray-600 mb-8">
                    {hasActiveListFilters
                      ? 'Essayez de modifier vos critères de recherche'
                      : creationOuverte
                        ? 'Commencez par créer vos premières matières et les associer aux classes'
                        : "Les matières apparaîtront ici dès qu'elles auront été créées."
                    }
                  </p>

                  {!hasActiveListFilters && creationOuverte && (
                    <button
                      onClick={() => {
                        // Scroll vers le formulaire
                        document.querySelector('[data-formulaire]')?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }}
                      className="inline-flex items-center px-6 py-3 bg-[#319190] text-white font-semibold rounded-lg hover:bg-[#63b5b4] transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Créer ma première matière
                    </button>
                  )}
                </div>
              ) : (
                // Table des matières
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {modeSelection && (
                          <th className="py-4 px-6">
                            <input
                              type="checkbox"
                              checked={paginatedData.length > 0 &&
                                paginatedData.every(item => selectedItems.includes(item.id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedItems([...new Set([...selectedItems, ...paginatedData.map(item => item.id)])]);
                                } else {
                                  setSelectedItems(selectedItems.filter(id =>
                                    !paginatedData.find(item => item.id === id)
                                  ));
                                }
                              }}
                              className="rounded border-gray-300 text-[#319190] focus:ring-[#319190]"
                            />
                          </th>
                        )}
                        <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 uppercase">Matière</th>
                        <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="py-4 px-6 text-center text-xs font-medium text-gray-500 uppercase">Coefficient</th>
                        <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 uppercase">Classe associée</th>
                        <th className="py-4 px-6 text-center text-xs font-medium text-gray-500 uppercase">Nb classes</th>
                        <th className="py-4 px-6 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedData.map(matiere => {
return (
                <tr key={matiere.id} className="hover:bg-gray-50 transition-colors">
                  {modeSelection && (
                    <td className="py-4 px-6">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(matiere.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems([...selectedItems, matiere.id]);
                          } else {
                            setSelectedItems(selectedItems.filter(id => id !== matiere.id));
                          }
                        }}
                        className="rounded border-gray-300 text-[#319190] focus:ring-[#319190]"
                      />
                    </td>
                  )}
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-[#319190] rounded-lg flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-900">{matiere.nom}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {matiere.code}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-sm font-semibold text-gray-900">{matiere.coefficient}</span>
                  </td>
                  <td className="py-4 px-6">
                    {/* ✅ NOUVELLE LOGIQUE D'AFFICHAGE */}
                    <div className="flex items-center space-x-2">
                      {matiere.classes_noms ? (
                        <>
                          <span className="text-gray-900 font-medium">{matiere.classes_noms}</span>
                        </>
                      ) : (
                        <span className="text-gray-400 italic">Non assignée</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    {(() => {
                      const classCount = getMatiereClassCount(matiere);
                      return (
                        <span className={classCount > 0
                          ? 'inline-flex min-w-[2.25rem] items-center justify-center rounded-full bg-[#246990]/10 px-2.5 py-1 text-xs font-semibold text-[#246990]'
                          : 'inline-flex min-w-[2.25rem] items-center justify-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500'}>
                          {classCount}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-4 px-6">
                        <div className="flex items-center justify-center space-x-2">
                          {droits.matieres.modifier && (
                            <button
                              onClick={() => setEditingItem({ ...matiere })}
                              className="p-2 text-gray-400 hover:text-[#319190] rounded-lg hover:bg-[#319190]/10 transition-colors"
                              title="Modifier les informations"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {/* Toujours rendu : sans le droit d'associer, la fenêtre
                              s'ouvre en lecture seule. */}
                          <button
                            onClick={() => openClassAssociationModal(matiere)}
                            className="p-2 text-gray-400 hover:text-[#246990] rounded-lg hover:bg-[#246990]/10 transition-colors"
                            title={droits.associer ? 'Gérer les classes associées' : 'Voir les classes associées'}
                            aria-label={droits.associer ? 'Gérer les classes associées' : 'Voir les classes associées'}
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                          {droits.matieres.supprimer && (
                            <button
                              onClick={() => handleDelete(matiere.id)}
                              className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>

                    </tr>
                  );
                })}

                    </tbody>
                  </table>

                  {/* Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                      setSelectedItems([]);
                    }}
                    onItemsPerPageChange={(value) => {
                      setItemsPerPage(value);
                      setCurrentPage(1);
                      setSelectedItems([]);
                    }}
                    itemsPerPage={itemsPerPage}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>


                  {/* ✅ NOUVEAU MODAL SIMPLIFIÉ */}
{/* ✅ REMPLACER L'ANCIEN MODAL PAR CELUI-CI */}
{editingItem && createPortal(
  <div className={`${CONFIRM_DIALOG_OVERLAY_CLASS} flex items-center justify-center p-4`}>
    <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 border-b border-[#246990]/10 bg-[#246990]/5 px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white shadow-sm">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-950">Modifier la matière</h3>
            <p className="mt-1 text-sm text-gray-500">{editingItem.nom}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditingItem(null)}
          disabled={loading}
          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-white hover:text-[#246990] disabled:opacity-50"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Nom de la matière</label>
            <input
              type="text"
              value={editingItem.nom}
              onChange={(e) => setEditingItem({ ...editingItem, nom: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Code matière</label>
              <input
                type="text"
                value={editingItem.code}
                onChange={(e) => setEditingItem({ ...editingItem, code: e.target.value.toUpperCase() })}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Coefficient</label>
              <input
                type="number"
                value={editingItem.coefficient}
                onChange={(e) => setEditingItem({ ...editingItem, coefficient: parseFloat(e.target.value) || 1 })}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10"
                min="0.5"
                step="0.5"
              />
            </div>
          </div>

          <div className="rounded-lg border border-[#246990]/10 bg-[#246990]/5 px-4 py-3">
            <div className="flex items-start gap-3">
              <Link2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#246990]" />
              <div>
                <p className="font-medium text-gray-900">Classes associées</p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Les associations se gèrent depuis l'action d'association dans la liste des matières.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
        <button
          type="button"
          onClick={() => setEditingItem(null)}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#246990]/30 hover:text-[#246990] disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={() => handleUpdate(editingItem.id, editingItem)}
          disabled={loading}
          className="inline-flex min-w-[132px] items-center justify-center rounded-lg bg-[#246990] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1f5a7d] disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Mise à jour...
            </>
          ) : (
            'Enregistrer'
          )}
        </button>
      </div>
    </div>
  </div>,
  document.body
)}
      </div>

      {/* Système de Toast */}
      <div className="fixed top-4 right-4 z-[10020] space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      {/* Dialog de confirmation */}
<ConfirmDialog
  isOpen={confirmDialog.isOpen}
  onClose={closeConfirmDialog}
  onConfirm={confirmDialog.onConfirm}
  title={confirmDialog.title}
  description={confirmDialog.description}
  confirmText={confirmDialog.confirmText}
  cancelText={confirmDialog.cancelText}
  type={confirmDialog.type}
/>


<ClassAssociationModal
  isOpen={classAssociationModal.isOpen}
  onClose={closeClassAssociationModal}
  onSave={saveClassAssociations}
  initialSelections={classAssociationModal.initialSelections}
  matiereId={classAssociationModal.matiereId}  // ⬅️ Doit être présent
  coefficientDefaut={classAssociationModal.coefficientDefaut}  // ⬅️ Doit être présent
  title={droits.associer
    ? `Associer des classes à "${classAssociationModal.matiereNom}"`
    : `Classes associées à "${classAssociationModal.matiereNom}"`}
  subtitle={droits.associer
    ? `Gérez les associations de la matière ${classAssociationModal.matiereCode} avec les différentes classes et leurs coefficients`
    : `Classes de la matière ${classAssociationModal.matiereCode} et leurs coefficients`}
  lectureSeule={!droits.associer}
  anneeScolaireId={activeYearId}
  sectionsData={sections}
  classesData={classes}
/>


    </div>
  );
};

export default MatieresManagement;
